import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FlaskConical, Library, Calculator, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    ingredients: 0,
    formulas: 0,
    stableFormulas: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [ingredientsRes, formulasRes] = await Promise.all([
        axios.get(`${API}/ingredients`),
        axios.get(`${API}/formulas`),
      ]);

      const stableCount = formulasRes.data.filter((f) => f.formula_type === 'stable').length;

      setStats({
        ingredients: ingredientsRes.data.length,
        formulas: formulasRes.data.length,
        stableFormulas: stableCount,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await axios.post(`${API}/seed-data`);
      toast.success('Données initiales chargées avec succès !');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du chargement des données');
    }
  };

  const quickActions = [
    {
      title: 'Nouveau calcul HLB',
      description: 'Calculer le HLB requis d\'une phase grasse',
      icon: Calculator,
      color: '#2F4840',
      action: () => navigate('/simulator'),
    },
    {
      title: 'Ajouter un ingredient',
      description: 'Enrichir la bibliotheque de matieres premieres',
      icon: FlaskConical,
      color: '#346A53',
      action: () => navigate('/ingredients'),
    },
    {
      title: 'Consulter les formules',
      description: 'Acceder aux formules stables et instables',
      icon: Library,
      color: '#B94A3E',
      action: () => navigate('/formulas'),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F4840]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-4xl font-semibold heading-font text-[#1A1C1B]" data-testid="dashboard-title">
          Tableau de bord
        </h2>
        <p className="text-[#5C615E] mt-2">
          Vue d'ensemble de votre laboratoire de formulation
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-[#E5E5DF]" data-testid="stat-ingredients">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#5C615E] text-sm font-semibold uppercase tracking-wide">
              <FlaskConical className="h-4 w-4" />
              Ingrédients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-semibold heading-font text-[#2F4840]">{stats.ingredients}</p>
            <p className="text-sm text-[#5C615E] mt-1">Matières premières</p>
          </CardContent>
        </Card>

        <Card className="border-[#E5E5DF]" data-testid="stat-formulas">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#5C615E] text-sm font-semibold uppercase tracking-wide">
              <Library className="h-4 w-4" />
              Formules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-semibold heading-font text-[#2F4840]">{stats.formulas}</p>
            <p className="text-sm text-[#5C615E] mt-1">Total de formules</p>
          </CardContent>
        </Card>

        <Card className="border-[#E5E5DF]" data-testid="stat-stable">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#5C615E] text-sm font-semibold uppercase tracking-wide">
              <TrendingUp className="h-4 w-4" />
              Formules stables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-semibold heading-font text-[#346A53]">{stats.stableFormulas}</p>
            <p className="text-sm text-[#5C615E] mt-1">Formules validées</p>
          </CardContent>
        </Card>
      </div>

      {stats.ingredients === 0 && (
        <Card className="border-[#E5E5DF] bg-[#E4EDE9]" data-testid="seed-data-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-medium text-[#1A1C1B] mb-2">
                  Commencez avec les données de référence
                </h3>
                <p className="text-sm text-[#5C615E] mb-4">
                  Charger les émulsionnants et huiles du document technique HLB
                </p>
              </div>
              <Button
                onClick={handleSeedData}
                className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                data-testid="seed-data-button"
              >
                Charger les données
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-2xl font-medium heading-font text-[#1A1C1B] mb-4">Actions rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Card
                key={index}
                className="border-[#E5E5DF] hover:border-[#2F4840] cursor-pointer transition-all duration-200"
                onClick={action.action}
                data-testid={`quick-action-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 rounded-sm"
                      style={{ backgroundColor: `${action.color}10` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: action.color }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-[#1A1C1B] mb-1">{action.title}</h4>
                      <p className="text-sm text-[#5C615E]">{action.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
